/**
 * RoomMaterials — what the room stores, as a tile grid (Step 11c, SPEC section 8 C).
 *
 * Built from the room DTO's `materials` (Nest, Step 10c) and extended live from the
 * loaded messages (no socket event). Room stimuli come first, then send order,
 * deduplicated by key or url. A PDF is sent with its rendered image as the message's
 * first image (composer contract): the pair is listed once, as the document.
 *
 * Images and videos open the shared creative preview; documents open in a new tab.
 * Sent items also offer "Show in chat". More than 8: seven tiles and a +N tile.
 */

import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FileText, MessageSquare, Play } from 'lucide-react'

import { CreativeLightbox, LightboxCreative } from '@/components/chat/CreativeLightbox'
import { CreativeMedia } from '@/components/chat/CreativeMedia'
import { scrollToChatMessage } from '@/components/chat/RoomTopics/scrollToChatMessage'
import { cn } from '@/lib/utils'

import { ChatRoomMaterial, ChatRoomMessageDto } from '@digital-twins/shared-types'

const COLLAPSED_LIMIT = 8

function materialIdentity(material: ChatRoomMaterial): string | null {
  return material.key ?? material.url
}

function materialsFromMessage(message: ChatRoomMessageDto): Array<ChatRoomMaterial> {
  const out: Array<ChatRoomMaterial> = []
  const base = { messageSequenceIndex: message.sequenceIndex }
  for (const key of message.imageKeys ?? []) {
    out.push({ kind: 'image', key, url: null, label: null, mimeType: null, ...base })
  }
  for (const video of message.videoRefs ?? []) {
    out.push({
      kind: 'video',
      key: null,
      url: video.url,
      label: video.label ?? null,
      mimeType: video.mimeType ?? null,
      ...base,
    })
  }
  for (const document of message.documentRefs ?? []) {
    out.push({
      kind: 'document',
      key: document.key,
      url: null,
      label: document.name,
      mimeType: document.mimeType,
      ...base,
    })
  }
  return out
}

/**
 * Room materials plus attachments of loaded messages the DTO does not list yet.
 * Stimuli first, then send order; one entry per key or url; a message's document
 * absorbs that message's first image (its render).
 */
export function mergeRoomMaterials(
  materials: Array<ChatRoomMaterial> | undefined,
  messages: Array<ChatRoomMessageDto>,
): Array<ChatRoomMaterial> {
  const seen = new Set<string>()
  const merged: Array<ChatRoomMaterial> = []
  const push = (material: ChatRoomMaterial) => {
    const identity = materialIdentity(material)
    if (!identity || seen.has(identity)) {
      return
    }
    seen.add(identity)
    merged.push(material)
  }
  for (const material of materials ?? []) {
    push(material)
  }
  const userMessages = messages
    .filter((message) => message.senderType === 'user' && message.sequenceIndex >= 0)
    .sort((a, b) => a.sequenceIndex - b.sequenceIndex)
  for (const message of userMessages) {
    for (const material of materialsFromMessage(message)) {
      push(material)
    }
  }

  const withDocument = new Set(
    merged
      .filter((material) => material.kind === 'document' && material.messageSequenceIndex !== null)
      .map((material) => material.messageSequenceIndex),
  )
  const absorbed = new Set<number>()
  const paired = merged.filter((material) => {
    const sequenceIndex = material.messageSequenceIndex
    if (
      material.kind !== 'image' ||
      sequenceIndex === null ||
      !withDocument.has(sequenceIndex) ||
      absorbed.has(sequenceIndex)
    ) {
      return true
    }
    absorbed.add(sequenceIndex)
    return false
  })

  return paired
    .map((material, order) => ({ material, order }))
    .sort((a, b) => {
      const aIndex = a.material.messageSequenceIndex ?? -1
      const bIndex = b.material.messageSequenceIndex ?? -1
      return aIndex - bIndex || a.order - b.order
    })
    .map(({ material }) => material)
}

interface RoomMaterialsProps {
  materials?: Array<ChatRoomMaterial>
  messages: Array<ChatRoomMessageDto>
  /** Readable URL of a material key (room route, or the guest share route). */
  getMaterialUrl: (key: string) => string
  /** Runs before a scroll or a preview (the phone sheet closes first). */
  onBeforeNavigate?: () => void
  onBackfill?: () => void | Promise<void>
}

export function RoomMaterials(props: RoomMaterialsProps) {
  const { materials, messages, getMaterialUrl, onBeforeNavigate, onBackfill } = props

  const { t } = useTranslation('app')
  const [isExpanded, setIsExpanded] = useState(false)
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null)

  const items = useMemo(() => mergeRoomMaterials(materials, messages), [materials, messages])

  const srcOf = (material: ChatRoomMaterial): string =>
    material.url ?? (material.key ? getMaterialUrl(material.key) : '')

  const previewable = items.filter((material) => material.kind !== 'document')
  const creatives: Array<LightboxCreative> = previewable.map((material) => ({
    src: srcOf(material),
    mimeType: material.mimeType ?? (material.kind === 'video' ? 'video/mp4' : undefined),
  }))

  if (items.length === 0) {
    return null
  }

  const isCollapsed = !isExpanded && items.length > COLLAPSED_LIMIT
  const visible = isCollapsed ? items.slice(0, COLLAPSED_LIMIT - 1) : items
  const hiddenCount = items.length - visible.length

  let imageNumber = 0
  let videoNumber = 0
  const labels = items.map((material) => {
    if (material.kind === 'document') {
      return t('roomPane.documentTile', { name: material.label ?? '' })
    }
    if (material.kind === 'video') {
      videoNumber += 1
      return material.label || t('roomPane.videoTile', { n: videoNumber })
    }
    imageNumber += 1
    return material.label || t('roomPane.imageTile', { n: imageNumber })
  })

  const onOpen = (material: ChatRoomMaterial) => {
    if (material.kind === 'document') {
      window.open(srcOf(material), '_blank', 'noopener,noreferrer')
      return
    }
    onBeforeNavigate?.()
    setLightboxIndex(previewable.indexOf(material))
  }

  const onShowInChat = (material: ChatRoomMaterial) => {
    if (material.messageSequenceIndex === null) {
      return
    }
    onBeforeNavigate?.()
    void scrollToChatMessage(String(material.messageSequenceIndex), { onBackfill })
  }

  return (
    <>
      <ul className="grid grid-cols-4 gap-2" aria-label={t('roomPane.materials')}>
        {visible.map((material, index) => {
          const isStimulus = material.messageSequenceIndex === null
          const caption =
            material.kind === 'document' ? material.label : isStimulus ? material.label : null
          return (
            <li key={materialIdentity(material) ?? index} className="group relative flex min-w-0 flex-col gap-1">
              <button
                type="button"
                data-testid={`room-material-${index}`}
                aria-label={labels[index]}
                onClick={() => onOpen(material)}
                className={cn(
                  'relative aspect-square w-full overflow-hidden rounded-lg border border-twin-chat-divider/60 bg-twin-chat-surface',
                  'focus:outline-none focus-visible:ring-2 focus-visible:ring-twin-chat-accent',
                )}
              >
                <MaterialThumb material={material} src={srcOf(material)} />
              </button>
              {caption && (
                <span className="truncate text-2xs text-twin-chat-muted" title={caption}>
                  {caption}
                </span>
              )}
              {!isStimulus && (
                <button
                  type="button"
                  data-testid={`room-material-${index}-show-in-chat`}
                  aria-label={t('roomPane.showInChat')}
                  title={t('roomPane.showInChat')}
                  onClick={() => onShowInChat(material)}
                  className={cn(
                    'absolute right-1 top-1 flex h-6 w-6 items-center justify-center rounded-full bg-twin-chat-surface/90 text-twin-chat-fg shadow-sm',
                    'opacity-0 transition-opacity group-hover:opacity-100 focus:opacity-100 [@media(hover:none)]:opacity-100',
                    'focus:outline-none focus-visible:ring-2 focus-visible:ring-twin-chat-accent',
                  )}
                >
                  <MessageSquare className="h-3 w-3" aria-hidden="true" />
                </button>
              )}
            </li>
          )
        })}
        {isCollapsed && (
          <li>
            <button
              type="button"
              data-testid="room-materials-more"
              aria-label={t('roomPane.materialsMore', { count: hiddenCount })}
              onClick={() => setIsExpanded(true)}
              className="flex aspect-square w-full items-center justify-center rounded-lg bg-twin-chat-bubble/40 text-sm font-semibold text-twin-chat-fg focus:outline-none focus-visible:ring-2 focus-visible:ring-twin-chat-accent"
            >
              +{hiddenCount}
            </button>
          </li>
        )}
      </ul>
      {lightboxIndex !== null && lightboxIndex >= 0 && (
        <CreativeLightbox
          creatives={creatives}
          index={lightboxIndex}
          onIndex={setLightboxIndex}
          onClose={() => setLightboxIndex(null)}
          dataTestId="room-materials-preview"
        />
      )}
    </>
  )
}

function MaterialThumb(props: { material: ChatRoomMaterial; src: string }) {
  const { material, src } = props

  if (material.kind === 'document') {
    return (
      <span className="flex h-full w-full items-center justify-center text-twin-chat-muted">
        <FileText className="h-5 w-5" aria-hidden="true" />
      </span>
    )
  }
  if (material.kind === 'video') {
    return (
      <>
        <CreativeMedia
          src={src}
          mimeType={material.mimeType ?? 'video/mp4'}
          alt=""
          className="h-full w-full object-cover"
        />
        <span
          aria-hidden="true"
          className="absolute inset-0 m-auto flex h-6 w-6 items-center justify-center rounded-full bg-media/60 text-media-foreground"
        >
          <Play className="h-3 w-3 fill-current" />
        </span>
      </>
    )
  }
  return <img src={src} alt="" loading="lazy" className="h-full w-full object-cover object-top" />
}
